import { DEBUG_ENABLED } from './DebugFlags';
import { debugDock, makeDraggable } from './DebugDock';

/**
 * Live input telemetry: a read-only projection of the real input path, rendered
 * once for two readers — the `#input-telemetry` overlay DOM and
 * `window.__THREE_GAME_DIAGNOSTICS__.input`. Both consume the exact same
 * snapshot so a screenshot and an automated assertion cannot disagree.
 *
 * This module never mutates input state: `InputController.readPressedKeys()`
 * returns a copy, the axis readers are pure, and the class below only writes to
 * its own DOM element and visibility flags.
 */

/** The control branch consuming the resolved axes this frame. */
export type InputBranch = 'deck' | 'helm' | 'sail' | 'build' | 'defeated' | 'offline' | 'ashore';

/** The last player-control message put on the wire, captured at the send site. */
export interface LastControlMessage {
  /** Message type as sent (e.g. `helm_input`, `deck_move`, `sail_rotate`). */
  type: string;
  /** Payload exactly as sent, after any frame conversion (helm turn is negated). */
  payload: Record<string, number | string | boolean>;
  /** Game elapsed seconds when the message was sent. */
  at: number;
}

/** Serialisable input snapshot shared by the overlay and diagnostics. */
export interface InputSnapshot {
  /** Held `KeyboardEvent.code` values, sorted. */
  keys: string[];
  /** Resolved horizontal axis in [-1, 1] (right = +1). */
  horizontal: number;
  /** Resolved vertical axis in [-1, 1] (down = +1). */
  vertical: number;
  /** Control branch consuming the axes. */
  branch: InputBranch;
  /** Authoritative station id the local player occupies (`''` on deck). */
  station: string;
  /** Human role label (deckhand / captain (helm) / sail N). */
  role: string;
  cameraMode: 'chase' | 'firstPerson';
  pointerLocked: boolean;
  lastControl: LastControlMessage | null;
}

/** Minimum time between DOM writes: ~10 Hz keeps debug layout cost off the hot path. */
const UI_UPDATE_MS = 100;

/** Render a numeric wire value; `turn` keeps its explicit sign for legibility. */
function formatValue(key: string, value: number | string | boolean): string {
  if (typeof value !== 'number') return String(value);
  if (!Number.isFinite(value)) return String(value);
  if (key === 'turn') {
    if (value === 0) return '0';
    return value > 0 ? `+${value}` : `${value}`;
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function formatPayload(payload: Record<string, number | string | boolean>): string {
  return Object.entries(payload)
    .map(([key, value]) => `${key}=${formatValue(key, value)}`)
    .join(' ');
}

/**
 * Pure formatter: one deterministic text block per snapshot. Unit-tested without
 * a browser, and the only place the overlay's text is produced, so the DOM and
 * the diagnostics always describe the same snapshot.
 */
export function formatInputSnapshot(snapshot: InputSnapshot): string {
  const sortedKeys = [...snapshot.keys].sort();
  const keys = sortedKeys.length > 0 ? sortedKeys.join(' ') : '·';
  const last = snapshot.lastControl
    ? `${snapshot.lastControl.type}${(() => {
        const payload = formatPayload(snapshot.lastControl.payload);
        return payload.length > 0 ? ` ${payload}` : '';
      })()}`
    : '—';
  const station = snapshot.station !== '' ? snapshot.station : 'deck';
  const lock = snapshot.pointerLocked ? 'locked' : 'off';
  return [
    `KEYS    ${keys}`,
    `AXES    H ${snapshot.horizontal.toFixed(2)}  V ${snapshot.vertical.toFixed(2)}`,
    `BRANCH  ${snapshot.branch}`,
    `STATION ${station} · ${snapshot.role}`,
    `CAMERA  ${snapshot.cameraMode} · lock ${lock}`,
    `LAST    ${last}`,
  ].join('\n');
}

/**
 * DOM overlay for `#input-telemetry`. Hidden by default, enabled by `?debug`
 * (through `DEBUG_ENABLED`) or forced by the `setInputOverlay` test hook, and
 * always suppressed on mobile profiles and while a capture is paused.
 */
export class InputTelemetry {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly title: HTMLElement;
  private readonly debugFlag: boolean;

  /** Test-hook override; `null` falls back to the `?debug` flag. */
  private forcedVisible: boolean | null = null;
  private mobile = false;
  private pausedForScreenshot = false;
  private lastUiUpdate = 0;
  private renderedText = '';

  constructor() {
    const root = document.getElementById('input-telemetry');
    const body = document.getElementById('input-telemetry-body');
    const title = document.getElementById('input-telemetry-title');
    if (!root || !body || !title) {
      throw new Error('Missing #input-telemetry elements in index.html.');
    }
    this.root = root;
    this.body = body;
    this.title = title;
    this.debugFlag = DEBUG_ENABLED;

    // Participate in the shared debug dock (master DEBUG button) only when
    // debug tooling is on, so a production page never gains the dock button.
    if (this.debugFlag) {
      makeDraggable(this.root, this.title);
      debugDock.register(this.root);
    }
    this.applyVisibility();
  }

  /** Suppress the overlay on touch/mobile profiles. */
  setMobile(mobile: boolean): void {
    this.mobile = mobile;
    this.applyVisibility();
  }

  /** Capture safety: never render while a screenshot is being framed. */
  setPausedForScreenshot(paused: boolean): void {
    this.pausedForScreenshot = paused;
    this.applyVisibility();
  }

  /** Test hook: force visibility, or `null` to restore the `?debug` default. */
  setForcedVisible(visible: boolean | null): void {
    this.forcedVisible = visible;
    this.applyVisibility();
  }

  /** True when the overlay should currently be visible. */
  isVisible(): boolean {
    return (this.forcedVisible ?? this.debugFlag) && !this.mobile && !this.pausedForScreenshot;
  }

  /** Render the snapshot, throttled to ~10 Hz and only when visible. */
  render(snapshot: InputSnapshot, now: number): void {
    if (!this.isVisible()) {
      if (!this.root.hidden) this.root.hidden = true;
      return;
    }
    if (now - this.lastUiUpdate < UI_UPDATE_MS) return;
    this.lastUiUpdate = now;
    const text = formatInputSnapshot(snapshot);
    if (text !== this.renderedText) {
      this.renderedText = text;
      this.body.textContent = text;
    }
  }

  dispose(): void {
    this.body.textContent = '';
    this.renderedText = '';
    this.root.hidden = true;
  }

  private applyVisibility(): void {
    this.root.hidden = !this.isVisible();
  }
}
